import React, { useState, useEffect } from "react";
import moment from "moment";
import { DateField } from "./DateField";
import { NumberField } from "./NumberField";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import type { UserInputValues } from "@/lib/utils/marketSimilarity/types";
import type { HistoricInputsResponse } from "@/lib/api/marketSimilarity";
import { Loader2 } from "lucide-react";

type Props = {
  loading: boolean;
  onSubmit: (userInputValues: UserInputValues) => void;
  defaultValues?: UserInputValues;
  commodityKeys: string[];
  onFetchHistoricInputs: (dateString: string, commodityKey: string, numRallyDays: number) => void;
  historicInputsLoading: boolean;
  historicInputsError: string | null;
  historicInputsData: HistoricInputsResponse | null;
};

export function UserInputsForm({
  loading,
  onSubmit,
  defaultValues,
  commodityKeys,
  onFetchHistoricInputs,
  historicInputsLoading,
  historicInputsError,
  historicInputsData,
}: Props) {
  const [inputValues, setInputValues] = useState<UserInputValues>({
    dateString: moment().format("YYYY-MM-DD")!,
    dateWeight: 1,
    ts2vecDate: moment().format("YYYY-MM-DD")!,
    ts2vecWeight: 1,
    stxUseScore: 0,
    stxUseScoreWeight: 1,
    seasonalScoreV1: 0,
    seasonalScoreV1Weight: 1,
    seasonalScoreV2: 0,
    seasonalScoreV2Weight: 1,
    mmScore: 0,
    mmScoreWeight: 1,
    algoScoreV3: 0,
    algoScoreV3Weight: 1,
    numRallyDays: 20,
    rallyPercentage: 0,
    rallyScoreWeight: 1,
    rsi: 0,
    rsiWeight: 1,
    clusterBy: "correl",
    clusterThreshold: undefined,
    commodityKey: "",
    ...defaultValues,
  });

  const [historicDate, setHistoricDate] = useState<string>("");

  // Get today's date in YYYY-MM-DD format for max date
  const todayDateString = new Date().toISOString().split("T")[0]!;

  // Auto-fill form fields when historic inputs data arrives
  useEffect(() => {
    if (historicInputsData) {
      setInputValues((prev) => ({
        ...prev,
        algoScoreV3: historicInputsData.algoScoreV3,
        mmScore: historicInputsData.mmScore,
        rsi: historicInputsData.rsi,
        stxUseScore: historicInputsData.stxUseScore,
        seasonalScoreV1: historicInputsData.seasonalScoreV1,
        seasonalScoreV2: historicInputsData.seasonalScoreV2,
        rallyPercentage: historicInputsData.rallyPercentageAsDecimal != null ? historicInputsData.rallyPercentageAsDecimal * 100: prev.rallyPercentage ?? 0,
        ts2vecDate: historicDate,
          /* numRallyDays: historicInputsData.numRallyDays */
      }));
    }
  }, [historicInputsData]);

  function update<K extends keyof UserInputValues>(
    key: K,
    value: UserInputValues[K]
  ) {
    setInputValues((prev) => ({ ...prev, [key]: value }));
  }

  // Map commodity keys to SelectOptionItem format
  const commodityOptions = commodityKeys.map((key) => ({
    value: key,
    label: key,
  }));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(inputValues);
      }}
    >
      {/* Historic Inputs - Compact horizontal bar */}
      <div className="mb-3 flex items-end gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex items-center gap-2 flex-1 min-w-0 justify-between">
          <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
            Load Historic:
          </span>
          <input
            type="date"
            className="flex-1 min-w-0 border rounded px-2 py-1.5 text-sm max-w-[160px]"
            value={historicDate}
            onChange={(e) => setHistoricDate(e.target.value)}
            max={todayDateString}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              if (historicDate && inputValues.commodityKey) {
                onFetchHistoricInputs(historicDate, inputValues.commodityKey, inputValues.numRallyDays);
              }
            }}
            disabled={
              !historicDate ||
              !inputValues.commodityKey ||
              historicInputsLoading
            }
            className="shrink-0"
          >
            {historicInputsLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              "Load"
            )}
          </Button>
        {historicInputsError && (
          <div
            role="alert"
            className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1 flex-shrink-0"
          >
            {historicInputsError}
          </div>
        )}
          </div>

        <Button type="submit" disabled={loading}>
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Fetch Similarity Data
        </Button>
        </div>

      </div>

      {/* Container */}
      <div className="flex md:flex-row flex-col gap-4">
        {/* Inputs Grid */}
        <div className="grid grid-rows-5 grid-flow-col gap-2 border-2 rounded-xl border-black p-4 w-full">
          <div>
            <label className="block text-sm font-medium mb-1">Commodity</label>
            <Combobox
              dwimOnChange
              options={commodityOptions}
              value={inputValues.commodityKey}
              onValueChange={(v) => update("commodityKey", v)}
              placeholder="Select commodity"
              className="w-full"
            />
          </div>

          <NumberField
            id="stxUseScore"
            label="stxUseScore"
            min={0}
            max={100}
            step={1}
            value={inputValues.stxUseScore}
            onValueChange={(v) => update("stxUseScore", v)}
          />

          <NumberField
            id="algoScoreV3"
            label="algoScoreV3"
            min={0}
            max={100}
            step={1}
            value={inputValues.algoScoreV3}
            onValueChange={(v) => update("algoScoreV3", v)}
          />

          <NumberField
            id="seasonalScoreV1"
            label="seasonalScoreV1"
            min={0}
            max={100}
            step={1}
            value={inputValues.seasonalScoreV1}
            onValueChange={(v) => update("seasonalScoreV1", v)}
          />

          <NumberField
            id="seasonalScoreV2"
            label="seasonalScoreV2"
            min={0}
            max={100}
            step={1}
            value={inputValues.seasonalScoreV2}
            onValueChange={(v) => update("seasonalScoreV2", v)}
          />

          <NumberField
            id="rsi"
            label="rsi"
            min={0}
            max={100}
            step={1}
            value={inputValues.rsi}
            onValueChange={(v) => update("rsi", v)}
          />

          <NumberField
            id="mmScore"
            label="mmScore"
            min={0}
            max={100}
            step={1}
            value={inputValues.mmScore}
            onValueChange={(v) => update("mmScore", v)}
          />

          <NumberField
            id="numRallyDays"
            label="numRallyDays"
            step={1}
            value={inputValues.numRallyDays}
            onValueChange={(v) => update("numRallyDays", Math.ceil(v))}
          />

          <NumberField
            id="rallyPercentage"
            label="rallyPercentage"
            step={1}
            value={inputValues.rallyPercentage}
            onValueChange={(v) => update("rallyPercentage", v)}
          />

          <div className="grid">
          <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
            Ts2Vec Date:
          </span>
          <input
            type="date"
            className="flex-1 min-w-0 border rounded px-2 py-1.5 text-sm"
                        value={inputValues.ts2vecDate}
            onChange={(v) => update("ts2vecDate", !!v.target.value ? moment(v.target.value).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"))}
                        max={todayDateString}
                    />
          </div>
                </div>

                {/* WeightsGrid */}
                <div className="grid grid-cols-2 gap-2 border-2 rounded-xl border-black p-4 w-full">
                    <NumberField
                        id="stxUseScoreWeight"
                        label="stxUseScoreWeight"
                        step={1}
                        value={inputValues.stxUseScoreWeight}
                        onValueChange={(v) => update("stxUseScoreWeight", v)}
                    />

                    <NumberField
                        id="algoScoreV3Weight"
                        label="algoScoreV3Weight"
                        step={1}
                        value={inputValues.algoScoreV3Weight}
                        onValueChange={(v) => update("algoScoreV3Weight", v)}
                    />

                    <NumberField
                        id="seasonalScoreV1Weight"
                        label="seasonalScoreV1Weight"
                        step={1}
                        value={inputValues.seasonalScoreV1Weight}
                        onValueChange={(v) => update("seasonalScoreV1Weight", v)}
                    />

                    <NumberField
                        id="seasonalScoreV2Weight"
                        label="seasonalScoreV2Weight"
                        step={1}
                        value={inputValues.seasonalScoreV2Weight}
                        onValueChange={(v) => update("seasonalScoreV2Weight", v)}
                    />

                    <NumberField
                        id="rsiWeight"
                        label="rsiWeight"
                        step={1}
                        value={inputValues.rsiWeight}
                        onValueChange={(v) => update("rsiWeight", v)}
                    />

                    <NumberField
                        id="mmScoreWeight"
                        label="mmScoreWeight"
                        step={1}
                        value={inputValues.mmScoreWeight}
                        onValueChange={(v) => update("mmScoreWeight", v)}
                    />

                    <NumberField
                        id="rallyScoreWeight"
                        label="rallyScoreWeight"
                        step={1}
                        value={inputValues.rallyScoreWeight}
                        onValueChange={(v) => update("rallyScoreWeight", v)}
                    />

                    <NumberField
                        id="ts2vecWeight"
                        label="ts2vecWeight"
                        step={1}
                        value={inputValues.ts2vecWeight}
                        onValueChange={(v) => update("ts2vecWeight", v)}
                    />
                </div>

                {/* Cluster Config */}
                <div className="grid grid-cols-1 grid-rows-3 flex-start gap-2 border-2 rounded-xl border-black p-2 w-full">
                    <div>
                        <label className="block text-sm font-medium mb-1">Cluster By</label>
                        <Combobox
                            dwimOnChange
                            options={[
                                { value: "euclidean", label: "Euclidean" },
                                { value: "correl", label: "Correl" },
                            ]}
                            value={inputValues.clusterBy}
                            onValueChange={(v) => {
                                update("clusterBy", v as "euclidean" | "correl");
                                // Set appropriate default threshold based on cluster type
                                update("clusterThreshold", v === "euclidean" ? 0.22 : 0.8);
                            }}
                            placeholder="Select cluster type"
                            className="w-full"
                        />
                    </div>

                    <NumberField
                        id="clusterThreshold"
                        label={`${inputValues.clusterBy === "euclidean" ? "Euclidean" : "Correl"} Threshold`}
                        min={0}
                        max={1}
                        step={0.01}
                        value={inputValues.clusterThreshold}
                        allowEmpty
                        onValueChange={(v) => update("clusterThreshold", v)}
                    />

                    {inputValues.clusterThreshold != null && (
                        <div className="mt-2 text-sm text-gray-600">
                            {inputValues.clusterBy === "euclidean" ? (
                                <>Threshold will include any dates with a value &le; the threshold</>
                            ) : (
                                <>Threshold will include any dates with a value &ge; the threshold</>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </form>
    );
}
